import * as React from 'react';
import { render, screen, fireEvent } from '@testing-library/react';
import Button from './Button';

describe('Button Component', () => {
  const mockOnClick = jest.fn();

  beforeEach(() => {
    mockOnClick.mockClear();
  });

  describe('Rendering as button', () => {
    it('renders with default props', () => {
      render(<Button onClick={mockOnClick}>Test Button</Button>);
      
      const button = screen.getByRole('button');
      expect(button).toBeInTheDocument();
      expect(button).toHaveTextContent('Test Button');
      expect(button).toHaveAttribute('data-context', 'Candidates');
      expect(button).toHaveAttribute('data-type', 'Primary');
      expect(button).toHaveAttribute('data-status', 'Default');
      expect(button).toHaveAttribute('data-size', 'M');
      expect(button).toHaveAttribute('data-mode', 'Light');
    });

    it('renders with custom props', () => {
      render(
        <Button
          onClick={mockOnClick}
          context="Companies"
          type="Secondary"
          size="L"
          mode="Dark"
          disabled
        >
          Custom Button
        </Button>
      );
      
      const button = screen.getByRole('button');
      expect(button).toHaveTextContent('Custom Button');
      expect(button).toHaveAttribute('data-context', 'Companies');
      expect(button).toHaveAttribute('data-type', 'Secondary');
      expect(button).toHaveAttribute('data-status', 'Disabled');
      expect(button).toHaveAttribute('data-size', 'L');
      expect(button).toHaveAttribute('data-mode', 'Dark');
      expect(button).toBeDisabled();
    });

    it('calls onClick when clicked', () => {
      render(<Button onClick={mockOnClick}>Click me</Button>);
      
      const button = screen.getByRole('button');
      fireEvent.click(button);
      
      expect(mockOnClick).toHaveBeenCalledTimes(1);
    });

    it('does not call onClick when disabled', () => {
      render(
        <Button onClick={mockOnClick} disabled>
          Disabled Button
        </Button>
      );
      
      const button = screen.getByRole('button');
      fireEvent.click(button);
      
      expect(mockOnClick).not.toHaveBeenCalled();
    });

    it('renders as submit button', () => {
      render(
        <Button onClick={mockOnClick} typeAttr="submit">
          Submit
        </Button>
      );
      
      const button = screen.getByRole('button');
      expect(button).toHaveAttribute('type', 'submit');
    });
  });

  describe('Rendering as link', () => {
    it('renders as anchor tag when href is provided', () => {
      render(<Button href="/profile">Go to Profile</Button>);
      
      const link = screen.getByRole('link');
      expect(link).toBeInTheDocument();
      expect(link).toHaveTextContent('Go to Profile');
      expect(link).toHaveAttribute('href', '/profile');
    });

    it('renders link with target and rel attributes', () => {
      render(
        <Button href="https://example.com" target="_blank">
          External Link
        </Button>
      );
      
      const link = screen.getByRole('link');
      expect(link).toHaveAttribute('href', 'https://example.com');
      expect(link).toHaveAttribute('target', '_blank');
      expect(link).toHaveAttribute('rel', 'noopener noreferrer');
    });

    it('renders link with custom rel attribute', () => {
      render(
        <Button href="/page" target="_blank" rel="nofollow">
          Custom Rel
        </Button>
      );
      
      const link = screen.getByRole('link');
      expect(link).toHaveAttribute('rel', 'nofollow');
    });

    it('applies all styling props to link', () => {
      render(
        <Button
          href="/jobs"
          context="Companies"
          type="Secondary"
          size="M"
          data-testid="link-button"
        >
          View Jobs
        </Button>
      );
      
      const link = screen.getByTestId('link-button');
      expect(link).toHaveAttribute('data-context', 'Companies');
      expect(link).toHaveAttribute('data-type', 'Secondary');
      expect(link).toHaveAttribute('data-size', 'M');
    });
  });

  describe('Icon rendering', () => {

    it('renders with left icon component', () => {
      const CustomIcon = () => <span data-testid='custom-icon'>I</span>;
      render(
        <Button onClick={mockOnClick} iconLeft={<CustomIcon />}>
          Back
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).toBeInTheDocument();
      expect(screen.getByTestId('custom-icon')).toBeInTheDocument();
    });

    it('renders with right icon component', () => {
      const CustomIcon = () => <span data-testid='custom-icon'>→</span>;
      render(
        <Button onClick={mockOnClick} iconRight={<CustomIcon />}>
          Next
        </Button>
      );

      const button = screen.getByRole('button');
      expect(button).toBeInTheDocument();
      expect(screen.getByTestId('custom-icon')).toBeInTheDocument();
    });

    it('renders with left icon by name', () => {
      render(
        <Button onClick={mockOnClick} iconLeftName='arrowLeft'>
          Back
        </Button>
      );

      const button = screen.getByRole('button');
      const icon = button.querySelector('span.iconLeft > span');
      expect(icon).toBeTruthy();
    });

    it('renders with right icon by name', () => {
      render(
        <Button onClick={mockOnClick} iconRightName='eye'>
          Next
        </Button>
      );

      const button = screen.getByRole('button');
      const icon = button.querySelector('span.iconRight > span');
      expect(icon).toBeTruthy();
    });

    it('renders with both icons via names', () => {
      render(
        <Button onClick={mockOnClick} iconLeftName='eyeClosed' iconRightName='eye'>
          Both Icons
        </Button>
      );

      const button = screen.getByRole('button');
      const icons = button.querySelectorAll('span.iconLeft > span, span.iconRight > span');
      expect(icons).toHaveLength(2);
    });

    it('renders icons on links', () => {
      render(
        <Button href="/profile" iconLeftName="user" iconRightName="chevronRight">
          Profile
        </Button>
      );

      const link = screen.getByRole('link');
      const icons = link.querySelectorAll('span.iconLeft > span, span.iconRight > span');
      expect(icons).toHaveLength(2);
    });

    it('prioritizes custom icon over icon name', () => {
      const CustomIcon = () => <span data-testid='custom-icon'>Custom</span>;
      render(
        <Button
          onClick={mockOnClick}
          iconLeft={<CustomIcon />}
          iconLeftName='eye'
        >
          Test
        </Button>
      );

      expect(screen.getByTestId('custom-icon')).toBeInTheDocument();
    });
  });

  describe('Styling and classes', () => {

    it('applies custom className', () => {
      render(
        <Button onClick={mockOnClick} className="custom-class">
          Custom Class
        </Button>
      );
      
      const button = screen.getByRole('button');
      expect(button).toHaveClass('custom-class');
    });

    it('sets data-testid', () => {
      render(
        <Button onClick={mockOnClick} data-testid="test-button">
          Test
        </Button>
      );
      
      expect(screen.getByTestId('test-button')).toBeInTheDocument();
    });
  });

  describe('Type variants', () => {

    const buttonTypes = ['Primary', 'Secondary', 'Terceary', 'Primary-Icon', 'Secondary-Icon', 'Terceary-Icon'] as const;
    
    buttonTypes.forEach(type => {
      it(`renders with type ${type}`, () => {
        render(
          <Button onClick={mockOnClick} type={type}>
            {type} Button
          </Button>
        );
        
        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-type', type);
      });
    });
  });

  describe('Size variants', () => {

    const buttonSizes = ['S', 'M', 'L'] as const;
    
    buttonSizes.forEach(size => {
      it(`renders with size ${size}`, () => {
        render(
          <Button onClick={mockOnClick} size={size}>
            Size {size}
          </Button>
        );
        
        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-size', size);
      });
    });
  });

  describe('Context variants', () => {

    const buttonContexts = ['Candidates', 'Companies'] as const;
    
    buttonContexts.forEach(context => {
      it(`renders with context ${context}`, () => {
        render(
          <Button onClick={mockOnClick} context={context}>
            {context} Button
          </Button>
        );
        
        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-context', context);
      });
    });
  });
});
